/* eslint-disable */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { PlayerProfileLink } from 'widgets/statspro/js/components/common/PlayerProfileLink';

/**
 * Renders an individual milestone player item with their milestone event details.
 *
 * Displays player name as a clickable link followed by the milestone event in parentheses.
 * Automatically adds commas between items except for the last item in the list.
 *
 * @param {object} props - The component props
 * @param {object} props.milestone - The milestone object containing player and event data
 * @param {string|number} props.milestone.playerId - Alternative player ID (fallback)
 * @param {string} props.milestone.milestoneEvent - The milestone event description (e.g., "100th game", "200th goal")
 * @param {object} props.milestone.player - The nested player object
 * @param {string|number} props.milestone.player.playerId - The player's unique identifier
 * @param {object} props.milestone.player.playerName - The player's name object
 * @param {string} props.milestone.player.playerName.givenName - The player's first name
 * @param {string} props.milestone.player.playerName.surname - The player's surname
 * @param {number} props.index - The item's position in the list (used for comma placement and fallback keys)
 * @param {number} props.listLength - Total number of items in the list (determines if comma should be added)
 * @param {boolean} props.isAFLW - Whether this is an AFLW match (affects link URL format for external players)
 * @param {string} props.teamLineupsContext - Context for tracking ('match-centre' or other)
 * @param {string} props.prefix - Prefix for generating unique keys (typically team identifier)
 * @returns {React.JSX.Element} - A Fragment containing the player link and milestone event
 */
export const TAMilestoneItem = ({
    milestone,
    index,
    listLength,
    isAFLW,
    teamLineupsContext,
    prefix
}) => {
    // Unique key fallback strategy using playerId/milestoneEvent or array index
    const playerId = milestone?.playerId || milestone?.player?.playerId;
    const milestoneEvent = milestone?.milestoneEvent
        ? `_${milestone.milestoneEvent}`
        : '';
    const uniqueKey = `TAMilestones__${prefix}__${
        playerId ?? index
    }${milestoneEvent}`;

    const trackingInfo = {
        event: PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.LINK,
        component_name:
            PULSE.app.common.CONSTANTS.TRACKING.VALUES.TEAM_LINEUPS.VIEW.FIELD,
        component_content_type:
            teamLineupsContext === 'match-centre'
                ? PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES.MATCH_CENTRE
                      .LINEUPS
                : PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES.LINEUPS
    };

    return (
        <Fragment key={uniqueKey}>
            <PlayerProfileLink
                classes="team-announcements__link"
                player={{
                    playerId: milestone?.player?.playerId,
                    firstName: milestone?.player?.playerName?.givenName,
                    surname: milestone?.player?.playerName?.surname
                }}
                trackingInfo={trackingInfo}
                isAFLW={isAFLW}
            >
                <span>
                    {milestone?.player?.playerName?.givenName}{' '}
                    {milestone?.player?.playerName?.surname}
                </span>
            </PlayerProfileLink>
            <span>
                {milestone?.milestoneEvent
                    ? ` (${milestone?.milestoneEvent})`
                    : null}
                {index + 1 !== listLength ? `, ` : null}{' '}
            </span>
        </Fragment>
    );
};

TAMilestoneItem.propTypes = {
    milestone: PropTypes.shape({
        playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        milestoneEvent: PropTypes.string,
        player: PropTypes.shape({
            playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            playerName: PropTypes.shape({
                givenName: PropTypes.string,
                surname: PropTypes.string
            })
        })
    }),
    index: PropTypes.number.isRequired,
    listLength: PropTypes.number.isRequired,
    isAFLW: PropTypes.bool,
    teamLineupsContext: PropTypes.string,
    prefix: PropTypes.string.isRequired
};
